import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Button } from 'atoms/buttons/Button';
import { Vertical } from 'atoms/layout/flex';
import { BodyL } from 'atoms/typography';
import { Img } from 'atoms/ui/Img';
import type { League, Pictures } from 'components/landing/types';
import { useConnectionContext } from 'contexts/connection';
import { useEvents } from 'lib/events/useEvents';
import { glossary } from 'lib/glossary';
import { laptopAndAbove } from 'style/mediaQuery';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column-reverse;
  justify-content: center;
  align-items: center;
  background-image: var(--background);
  background-size: cover;
  background-position: center bottom;
  border-radius: 20px 20px 0 0;
  padding: var(--triple-unit);
  gap: var(--double-unit);
  @media (${laptopAndAbove}) {
    min-height: 900px;
    flex-direction: row;
    gap: calc(var(--unit) * 12);
  }
`;

const TextContainer = styled(Vertical)`
  align-items: flex-start;
  max-width: 450px;
`;

const TitleText = styled.h2`
  font-size: var(--fs-landing-title);
  line-height: 1;
  font-family: var(--brand-font);
  text-transform: uppercase;
`;

const StyledImgWrapper = styled.div`
  @media (${laptopAndAbove}) {
    align-self: flex-end;
  }
  img {
    width: 100%;
    max-width: 532px;

    height: auto;
    object-fit: cover;
  }
`;

type Props = {
  assets: {
    background: Pictures;
    lineup: Pictures;
  };
  league: League;
  messages: {
    play: MessageDescriptor;
    playDescription: MessageDescriptor;
  };
};

export const Play = ({ assets, league, messages }: Props) => {
  const { signUp } = useConnectionContext();
  const track = useEvents();

  const onClick = () => {
    track('Click Landing Page Play', { clickSource: league });
    signUp();
  };
  return (
    <Wrapper
      style={
        {
          '--background': `image-set(url(${assets.background.avif}) type('image/avif'), url(${assets.background.pictureUrl}) type('image/png'))`,
        } as CSSProperties
      }
    >
      <StyledImgWrapper>
        <picture>
          <source srcSet={assets.lineup.avif} type="image/avif" />
          <source srcSet={assets.lineup.pictureUrl} type="image/png" />
          <Img src={assets.lineup.pictureUrl} alt="" width="532" height="699" />
        </picture>
      </StyledImgWrapper>
      <TextContainer gap={5}>
        <Vertical gap={2}>
          <TitleText>
            <FormattedMessage {...messages.play} />
          </TitleText>
          <BodyL>
            <FormattedMessage {...messages.playDescription} />
          </BodyL>
        </Vertical>
        <Button color="secondary" size="large" onClick={onClick}>
          <FormattedMessage {...glossary.playNow} />
        </Button>
      </TextContainer>
    </Wrapper>
  );
};
